import { ShallowRef, getCurrentScope, onScopeDispose, shallowRef } from 'vue';

export function useInterval(
  handler: () => void,
  interval: number,
  options?: { immediate: boolean }
): void {
  const effectScope = getCurrentScope();

  if (effectScope === undefined) {
    throw new Error('`useInterval` composable must be called from within setup context');
  }

  effectScope.run(() => {
    if (options?.immediate) handler();
    const timerId = window.setInterval(handler, interval);
    onScopeDispose(() => window.clearInterval(timerId));
  });
}

export function useEventListener<K extends keyof WindowEventMap>(
  type: K,
  listener: (this: Window, ev: WindowEventMap[K]) => void,
  options?: boolean | AddEventListenerOptions
): void {
  const effectScope = getCurrentScope();

  if (effectScope === undefined) {
    throw new Error('`useEventListener` composable must be called from within setup context');
  }

  effectScope.run(() => {
    addEventListener(type, listener, options);
    onScopeDispose(() => removeEventListener(type, listener, options));
  });
}

export function useIsOnline(): ShallowRef<boolean> {
  const isOnline = shallowRef(navigator.onLine);

  function updateIsOnline() {
    isOnline.value = navigator.onLine;
  }

  useEventListener('online', updateIsOnline);
  useEventListener('offline', updateIsOnline);

  return isOnline;
}
